/**
 * Hook for toggling follow status on a document
 * 
 * Uses optimistic updates: UI updates immediately, then syncs with backend.
 * Reverts on error.
 */

import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { erp } from "@/lib/erp";

type UseToggleFollowParams = {
  doctype: string;
  name: string;
  initialFollowed: boolean;
};

/**
 * Hook for toggling follow status on a document
 * 
 * Uses optimistic updates: UI updates immediately, then syncs with backend.
 * Also invalidates the sidebar query to refresh follow status.
 */
export function useToggleFollow({ doctype, name, initialFollowed }: UseToggleFollowParams) {
  const queryClient = useQueryClient();
  const [localFollowed, setLocalFollowed] = useState(initialFollowed);

  // Sync local state when initialFollowed changes (e.g., after sidebar refetch)
  useEffect(() => {
    setLocalFollowed(initialFollowed);
  }, [initialFollowed]);

  const mutation = useMutation({
    mutationFn: async (nextFollowed: boolean) => {
      return erp.docs.toggleFollow(doctype, name, nextFollowed);
    },
    onSuccess: (data) => {
      // Sync local state with server truth (backend returns actual state)
      setLocalFollowed(data.following);

      // Invalidate sidebar query to refresh everything else
      queryClient.invalidateQueries({
        queryKey: ["doc-sidebar", doctype, name],
      });
    },
    onError: () => {
      // Revert on error
      setLocalFollowed((prev) => !prev);
    },
  });

  const toggle = () => {
    const next = !localFollowed;
    setLocalFollowed(next); // Optimistic update
    mutation.mutate(next);
  };

  return {
    followed: localFollowed,
    toggle,
    isLoading: mutation.isPending,
  };
}

